import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { rentalUnderwritingEnabled } from '../../config/featureFlags';
import { focusRing, scrollToId } from './landingStyles';

const audiences = [
  {
    title: 'New investors',
    body: 'Guided setup, plain-language explanations, and a readiness checklist so you can see which assumptions are still missing.',
  },
  {
    title: 'Active flippers',
    body: 'Purchase, rehab, financing, and holding costs stay on one deal, with stress tests when sale price or rehab moves.',
  },
  {
    title: 'Screening acquisitions',
    body: 'Use Comp Finder or Deal Analyzer on a property before you decide to save a full deal file.',
  },
  {
    title: 'Experienced operators',
    body: 'Portfolio filters, modeled grade, scenario presets, and an AI review grounded in the evidence saved on the deal.',
  },
];

export const AudienceSection: React.FC = () => (
  <Box component="section" aria-labelledby="audience-title">
    <Typography id="audience-title" variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1 }}>
      Built for new investors and operators
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
      {rentalUnderwritingEnabled()
        ? 'Model a flip, a buy-and-hold, or a BRRRR from the assumptions you enter. Rental results are a saved model, not a lender approval or a recommendation to buy.'
        : 'The same flip and resale model serves a first deal and a larger pipeline. DealSkout does not underwrite rental income.'}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: 2,
      }}
    >
      {audiences.map((item) => (
        <Box
          key={item.title}
          sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            {item.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.body}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const spotlights = [
  {
    title: 'Comparable sales',
    body: 'Enter comps yourself, import a spreadsheet, or run an automatic search. A comp-based value indication compares those sales with the ARV you entered. It is not an appraisal.',
  },
  {
    title: 'Risk and stress testing',
    body: 'Presets rerun the same deal calculation when sale price, rehab, or holding time changes. Results are modeled scenarios, not a forecast.',
  },
  {
    title: 'AI Deal Review',
    body: 'The review explains strengths, concerns, and next steps from evidence already saved on the deal. It does not change your numbers, comps, or ARV.',
  },
  {
    title: 'Portfolio, grade, and readiness',
    body: 'Organize deals, filter the pipeline, and see a modeled grade plus which assumptions are still incomplete. The grade is not a recommendation to purchase.',
  },
];

export const SpotlightSection: React.FC = () => (
  <Box component="section" aria-labelledby="spotlight-title">
    <Typography id="spotlight-title" variant="h4" component="h2" sx={{ fontWeight: 800, mb: 3 }}>
      What you can do with a deal
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      {spotlights.map((item) => (
        <Box
          key={item.title}
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderTop: '3px solid',
            borderTopColor: 'brand.yellow',
          }}
        >
          <Typography variant="h6" component="h3" sx={{ fontWeight: 800, mb: 1 }}>
            {item.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.body}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export const LearnTeaser: React.FC = () => (
  <Box component="section" aria-labelledby="learn-teaser-title">
    <Typography id="learn-teaser-title" variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1 }}>
      Learning Center
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
      Read how DealSkout defines profit, comps, rehab, stress tests, and the modeled grade. The
      same explanations are available while you work a deal.
    </Typography>
    <Button component={RouterLink} to="/learn" variant="outlined" color="primary" sx={focusRing}>
      Open the Learning Center
    </Button>
  </Box>
);

const faqs = [
  {
    q: 'Do I need a credit card to start?',
    a: 'No. The Free plan does not require a card. Paid checkout is optional.',
  },
  {
    q: 'Are the results a recommendation to buy?',
    a: 'No. Profit, grade, comp indication, and stress tests are modeled from your assumptions. They are not a recommendation to purchase.',
  },
  {
    q: 'Does AI Deal Review change my numbers?',
    a: 'No. A review explains evidence already saved on the deal. It does not edit the deal, comps, or ARV.',
  },
  {
    q: 'What do Free and Pro include?',
    a: 'Allowances come from the plan catalog: AI Deal Reviews and automatic comp searches per period. A repeated search that is still cached does not use another automatic search.',
  },
];

export const FaqSection: React.FC = () => (
  <Box component="section" aria-labelledby="faq-title">
    <Typography id="faq-title" variant="h4" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
      Questions
    </Typography>
    <Box sx={{ display: 'grid', gap: 2 }}>
      {faqs.map((item) => (
        <Box key={item.q}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {item.q}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.a}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export const PublicFooter: React.FC = () => (
  <Box
    component="footer"
    sx={{
      mt: 4,
      pt: 3,
      borderTop: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 2,
      alignItems: 'center',
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 800, mr: 'auto' }}>
      DealSkout
    </Typography>
    <Button component="a" href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToId('how-it-works'); }}>
      How it works
    </Button>
    <Button component="a" href="#pricing" onClick={(e) => { e.preventDefault(); scrollToId('pricing'); }}>
      Pricing
    </Button>
    <Button component={RouterLink} to="/learn">
      Learn
    </Button>
    <Button component="a" href="#sign-in" onClick={(e) => { e.preventDefault(); scrollToId('sign-in'); }}>
      Sign in
    </Button>
    <Button component={RouterLink} to="/signup" variant="contained">
      Start free
    </Button>
  </Box>
);
